import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { toast } from "react-toastify";
import { useLazyQuery } from "@apollo/client";
import Typography from "@mui/material/Typography";

// Custom.
import Layout from "containers/Layout/Layout";
import { GeneralObject, I18n } from "@vocollege/app";
import { GET_GENERAL_STATISTICS } from "@vocollege/app/dist/modules/VoApi/graphql";
import Content from "containers/Statistics/Content";
import { DownloadExcelButtonProps } from "@vocollege/components/dist/DownloadExcelButton";
import { getExcelData } from "../Utils";

const General: React.FC = () => {
  const [queryFilters, setQueryFilters] = useState<GeneralObject | null>(null);
  const [excelData, setExcelData] = useState<DownloadExcelButtonProps | null>(
    null
  );

  // Methods.

  const handleChange = (filters: GeneralObject) => {
    try {
      setQueryFilters(filters);
      load({
        variables: {
          filters,
        },
      });
    } catch (error: any) {
      toast.error(error.message, { autoClose: false });
    }
  };

  const getEducatorNumber = (educatorType: string) => {
    if (
      !data?.statistics?.educators ||
      data.statistics.educators?.length === 0
    ) {
      return 0;
    }
    return data.statistics.educators.filter((v: GeneralObject) => {
      return v?.educator_type?.indexOf(educatorType) > -1;
    }).length;
  };

  const getEmployersNumberByType = (employerType: string) => {
    return data.statistics.employers.filter((v: GeneralObject) => {
      return v.employer_type?.indexOf(employerType) > -1;
    }).length;
  };

  // Api.

  const [load, { data, loading, error }] = useLazyQuery(
    GET_GENERAL_STATISTICS,
    {
      fetchPolicy: "network-only",
    }
  );

  // Effects.

  useEffect(() => {
    if (data) {
      setExcelData(
        getExcelData(data, queryFilters, I18n.get.statistics.labels.generalStatistics)
      );
    }
  }, [data, queryFilters]);

  return (
    <Layout>
      <Content
        loading={loading}
        error={error}
        data={data}
        HeaderProps={{
          title: I18n.get.statistics.labels.generalStatistics,
          onChange: handleChange,
          fields: ["to"],
          excel: excelData || undefined,
        }}>
        <Stack spacing={2}>
          <Stack direction="row">
            <Typography component="h2" variant="h5" sx={{ flex: 1 }}>
              {I18n.get.statistics.labels.generalStatistics}
            </Typography>

            <Typography component="h3" variant="body1">
              {queryFilters?.to}
            </Typography>
          </Stack>

          {data?.statistics?.regions && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs="auto" sx={{ width: 300 }}>
                  <Typography variant="body1">
                    {I18n.get.statistics.labels.numberOfRegionalVoCollege}
                  </Typography>
                </Grid>
                <Grid item xs sx={{ textAlign: "right" }}>
                  <Typography variant="body1">
                    {data.statistics.regions.length}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          {data?.statistics?.localColleges && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs="auto" sx={{ width: 300 }}>
                  <Typography variant="body1">
                    {I18n.get.statistics.labels.numberOfLocalVoCollege}
                  </Typography>
                </Grid>
                <Grid item xs sx={{ textAlign: "right" }}>
                  <Typography variant="body1">
                    {data.statistics.localColleges.length}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          {data?.statistics?.employers && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs="auto" sx={{ width: 300 }}>
                  <Typography variant="body1">
                    {I18n.get.statistics.labels.numberOfEmployer}
                  </Typography>
                </Grid>
                <Grid item xs sx={{ textAlign: "right" }}>
                  <Typography variant="body1">
                    {data.statistics.employers.length}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {data?.statistics?.educators && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs="auto" sx={{ width: 300 }}>
                  <Typography variant="body1">
                    {I18n.get.statistics.labels.numberOfEducators}
                  </Typography>
                </Grid>
                <Grid item xs sx={{ textAlign: "right" }}>
                  <Typography variant="body1">
                    {data.statistics.educators.length}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {data?.statistics?.employers && (
            <Box>
              <Typography variant="h6" component="h5" paddingTop={2}>
                {I18n.get.statistics.labels.employerPerType}
              </Typography>
              <Typography
                variant="caption"
                component="h6"
                lineHeight={1.3}
                marginBottom={2}>
                {I18n.get.statistics.messages.employerCanAppearMultipleTimes}
              </Typography>
              <Stack spacing={1} sx={{ ml: 3 }}>
                {Object.keys(I18n.get.group.employerTypes).map((v: string) => (
                  <Box key={v}>
                    <Grid container spacing={2}>
                      <Grid item xs="auto" sx={{ width: 300 }}>
                        <Typography variant="body2">
                          {I18n.get.group.employerTypes[v]}
                        </Typography>
                      </Grid>
                      <Grid item xs sx={{ textAlign: "right" }}>
                        <Typography variant="body2">
                          {getEmployersNumberByType(v)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {data?.statistics?.educators && (
            <Box>
              <Typography variant="h6" component="h5" paddingTop={2}>
                {I18n.get.statistics.labels.educatorPerType}
              </Typography>
              <Typography
                variant="caption"
                component="h6"
                lineHeight={1.3}
                marginBottom={2}>
                {I18n.get.statistics.messages.educatorCanAppearMultipleTimes}
              </Typography>
              <Stack spacing={1} sx={{ ml: 3 }}>
                {Object.keys(I18n.get.group.educatorTypes).map((v: string) => (
                  <Box key={v}>
                    <Grid container spacing={2}>
                      <Grid item xs="auto" sx={{ width: 300 }}>
                        <Typography variant="body2">
                          {I18n.get.group.educatorTypes[v]}
                        </Typography>
                      </Grid>
                      <Grid item xs sx={{ textAlign: "right" }}>
                        <Typography variant="body2">
                          {getEducatorNumber(v)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {data?.statistics?.educations && (
            <Box>
              <Typography variant="h6" paddingTop={2}>
                {I18n.get.education.labels.educations}
              </Typography>
              <Typography
                variant="caption"
                component="h6"
                lineHeight={1.3}
                marginBottom={2}>
                {
                  I18n.get.statistics.messages
                    .sumOfEducationsCanBeDifferentFromNumberOfEducators
                }
              </Typography>

              <Stack spacing={1} sx={{ ml: 3 }}>
                {data.statistics.educations.map((v: GeneralObject) => (
                  <Box key={v.id}>
                    <Grid container spacing={2}>
                      <Grid item xs="auto" sx={{ width: 300 }}>
                        <Typography variant="body2">{v.title}</Typography>
                      </Grid>
                      <Grid item xs sx={{ textAlign: "right" }}>
                        <Typography variant="body2">
                          {v.total_usages}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Content>
    </Layout>
  );
};

export default General;
